/**
 * Company Overview Tab Component - Company info and capabilities.
 * CR-4821: Performance Summary has been removed from this tab; all performance
 * data lives on the Performance tab (single source of truth).
 */

import { useState, type ReactNode } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Building2,
  Mail,
  Globe,
  MapPin,
  Users,
  Calendar,
  DollarSign,
  Award,
  Clock,
  Phone,
  Hash,
  FileText,
  Pencil,
  type LucideIcon,
} from 'lucide-react';
import { PrimaryContactRow } from './PrimaryContactRow';
import { SupplierProfile, Company } from '../../lib/api';
import { useFormatters } from '../../contexts/PreferencesContext';
import { useMenuSettings } from '../../contexts/MenuSettingsContext';
import { CapabilitiesEditor } from './CapabilitiesEditor';
import { EditCompanyDialog, CompanyEditForm } from './EditCompanyDialog';
import { toast } from 'sonner';
import { cleanErpText } from '../../utils/erpText';

interface OverviewTabProps {
  supplier: SupplierProfile;
  /** CR-4815 r6: full Company for the card + edit dialog. The embedded
   * supplier.company_details is the slim CompanyNestedSerializer (CWE-359 PII
   * strip) and omits registration_number / contact / address_* fields. Null
   * when the rich fetch was unavailable — render falls back to the slim embed. */
  companyInfo?: Company | null;
  canEdit: boolean;
  onUpdateProfile: (data: Partial<SupplierProfile>) => Promise<void>;
  onUpdateCompany: (data: Record<string, string | number | boolean | null>) => Promise<void>;
}

// CR-4815 round 3: extract a human-readable message from any of the
// error shapes the API can throw — Helix envelope (details.fields),
// flat DRF (field -> [errors]), Axios error.response.data.detail, or
// plain Error/string. Used by both per-call and outer catch blocks
// so the toast is always informative.
function extractErrorMessage(err: unknown): string {
  if (err instanceof Error) {
    const axiosErr = err as { response?: { data?: Record<string, unknown> } };
    const data = axiosErr.response?.data;
    if (data && typeof data === 'object') {
      const details = (data['details'] as Record<string, unknown> | undefined);
      const envelopeFields = details?.['fields'] as Record<string, unknown> | undefined;
      if (envelopeFields && typeof envelopeFields === 'object') {
        const fieldErrors = Object.entries(envelopeFields)
          .map(([f, v]) => `${f}: ${Array.isArray(v) ? v.join(', ') : String(v)}`)
          .join('; ');
        if (fieldErrors) return fieldErrors;
      }
      if (typeof data['detail'] === 'string') return data['detail'] as string;
      const flatFieldErrors = Object.entries(data)
        .filter(([, v]) => Array.isArray(v) || typeof v === 'string')
        .map(([f, v]) => `${f}: ${Array.isArray(v) ? v.join(', ') : v}`)
        .join('; ');
      if (flatFieldErrors) return flatFieldErrors;
    }
    return err.message;
  }
  if (typeof err === 'string') return err;
  return 'Unknown error';
}

/**
 * A single labelled field row in the Company Information card: an icon, a label,
 * and its value (plain text or richer content such as a link).
 */
function InfoRow({ icon: Icon, label, children }: { icon: LucideIcon; label: string; children: ReactNode }) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="h-4 w-4 text-muted-foreground mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium">{label}</p>
        <div className="text-sm text-muted-foreground break-words">{children}</div>
      </div>
    </div>
  );
}

export function OverviewTab({ supplier, companyInfo, canEdit, onUpdateProfile, onUpdateCompany }: OverviewTabProps) {
  // CR-4815 r6: format the supplier's monetary fields in the SUPPLIER'S own
  // currency, not the viewer's preference. The viewer-scoped formatCurrency
  // prop made a GBP supplier's revenue render in the viewer's effective
  // currency (e.g. € under a EUR view) — the symptom QA flagged. The
  // currency-aware formatter from the hook does no FX conversion, so the raw
  // value renders with the supplier's own symbol.
  const { formatDate, formatCurrency } = useFormatters();
  const { supplierProfileLayout } = useMenuSettings();
  // CR-4815 r6: prefer the full Company for the fields the slim company_details
  // embed strips (registration_number, contact, address_*). Fall back to the
  // slim embed for the handful of fields it carries when the rich fetch was
  // unavailable. This is the single source of truth for both the card and the
  // edit dialog so a saved value surfaces immediately after PATCH.
  const cd: Company = companyInfo ?? supplier.company_details;
  // ERP-sourced contact fields can arrive URL-encoded ("12+High+Street") or
  // as the literal "None"; tidy them for display only.
  const displayPhone = cleanErpText(cd.primary_phone);
  const fullAddress =
    [cd.address_line_1, cd.address_line_2, cd.city, cd.state, cd.postal_code, cd.country]
      .map((part) => cleanErpText(part))
      .filter(Boolean)
      .join(', ') || null;
  const showCapabilities = supplierProfileLayout.overview.capabilities;

  // CR-4819: Company Information edit dialog lives on Overview (moved from Settings)
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState<CompanyEditForm>({
    name: '',
    registration_number: '', payment_terms: '', currency: '', annual_revenue: '', employee_count: '',
    year_established: '', primary_email: '', primary_phone: '', website: '',
    address_line_1: '', address_line_2: '', city: '', state: '', postal_code: '', country: '',
  });

  const openEditDialog = () => {
    // CR-4815 r6: seed every input from the full Company resolved above
    // (``cd``). Earlier rounds fetched the rich company here on dialog open;
    // it's now fetched once at the page level and shared with the card, so the
    // dialog and the card can never disagree. ``cd`` already falls back to the
    // slim embed when the rich fetch was unavailable.
    setEditForm({
      name: cd.name || '',
      registration_number: cd.registration_number || '',
      payment_terms: supplier.payment_terms || '',
      currency: supplier.currency || '',
      annual_revenue: supplier.annual_revenue || '',
      employee_count: supplier.employee_count?.toString() || '',
      year_established: supplier.year_established?.toString() || '',
      primary_email: cd.primary_email || '',
      primary_phone: cd.primary_phone || '',
      website: cd.website || '',
      address_line_1: cd.address_line_1 || '',
      address_line_2: cd.address_line_2 || '',
      city: cd.city || '',
      state: cd.state || '',
      postal_code: cd.postal_code || '',
      country: cd.country || '',
    });
    setEditDialogOpen(true);
  };

  const handleSave = async () => {
    // CR-4815 round 3: defensive instrumentation. Sarah's MAB-staging
    // repro on 2026-05-20 reported a silent no-op — dialog stays open,
    // no toast, nothing saved. None of the branches below should be
    // capable of producing that symptom, so log every checkpoint so we
    // can read the browser console next round if the bug persists.
    // eslint-disable-next-line no-console
    console.info('[CR-4815] handleSave start', { supplierId: supplier?.id, hasSupplier: !!supplier });
    if (!supplier) {
      toast.error('Supplier not loaded yet — please reopen the dialog and try again.');
      return;
    }
    setSaving(true);
    let dialogClosed = false;
    const closeDialog = () => { if (!dialogClosed) { dialogClosed = true; setEditDialogOpen(false); } };
    try {
      // CR-4815 round 2: build a real delta — only send fields the user
      // actually changed. The previous version PATCHed every dialog field on
      // every save, which:
      //   * sent currency: "" when the supplier had no currency configured
      //     (SupplierProfile.currency is CharField(max_length=3) with no
      //     blank=True, so DRF rejected "" with "This field may not be
      //     blank") — that single field killed the whole SupplierProfile
      //     PATCH after the Company PATCH had already landed, producing the
      //     partial-save Jacqueline reported in round 2;
      //   * sent unchanged emails / urls back through DRF EmailField /
      //     URLField validators which sometimes flag values our model
      //     accepted historically but DRF round-trip rejects.
      // CR-4815 r6: diff against the full Company (``cd``), not the slim
      // company_details embed. The embed strips registration_number / contact /
      // address_* to undefined, so diffing against it flagged unchanged fields
      // as changed and shipped them on every save. ``cd`` is the same source the
      // dialog seeds from, so the delta now contains only real edits.
      const company = cd;
      const companyDelta: Record<string, string | number | boolean | null> = {};
      const addCompany = (key: string, current: string | undefined, formValue: string) => {
        if ((current ?? '') !== formValue) companyDelta[key] = formValue;
      };
      // Name only ships when non-blank: clearing the field must not attempt
      // to unset a required column (DRF would reject and fail the PATCH).
      if (editForm.name.trim()) {
        addCompany('name', company.name, editForm.name.trim());
      }
      addCompany('registration_number', company.registration_number, editForm.registration_number);
      addCompany('primary_email', company.primary_email, editForm.primary_email);
      addCompany('primary_phone', company.primary_phone, editForm.primary_phone);
      addCompany('website', company.website, editForm.website);
      addCompany('address_line_1', company.address_line_1, editForm.address_line_1);
      addCompany('address_line_2', company.address_line_2, editForm.address_line_2);
      addCompany('city', company.city, editForm.city);
      addCompany('state', company.state, editForm.state);
      addCompany('postal_code', company.postal_code, editForm.postal_code);
      addCompany('country', company.country, editForm.country);

      const profileDelta: Partial<SupplierProfile> = {};
      if ((supplier.payment_terms ?? '') !== editForm.payment_terms) {
        profileDelta.payment_terms = editForm.payment_terms;
      }
      if (editForm.currency && (supplier.currency ?? '') !== editForm.currency) {
        // currency is required on SupplierProfile (no blank=True) — never
        // ship an empty string even if the user opens + closes the dropdown.
        profileDelta.currency = editForm.currency;
      }
      if (editForm.annual_revenue && (supplier.annual_revenue ?? '') !== editForm.annual_revenue) {
        profileDelta.annual_revenue = editForm.annual_revenue;
      }
      const employeeCount = editForm.employee_count ? (parseInt(editForm.employee_count) || null) : null;
      if (employeeCount !== (supplier.employee_count ?? null)) {
        profileDelta.employee_count = employeeCount;
      }
      const yearEstablished = editForm.year_established ? (parseInt(editForm.year_established) || null) : null;
      if (yearEstablished !== (supplier.year_established ?? null)) {
        profileDelta.year_established = yearEstablished;
      }

      // eslint-disable-next-line no-console
      console.info('[CR-4815] computed deltas', {
        companyDeltaKeys: Object.keys(companyDelta),
        profileDeltaKeys: Object.keys(profileDelta),
      });

      // CR-4815 round 3: per-call try/catch so a failure on one side
      // doesn't swallow success on the other AND so the user always
      // sees a toast that names which side failed.
      const failures: string[] = [];
      const successes: string[] = [];
      if (Object.keys(profileDelta).length > 0) {
        try {
          await onUpdateProfile(profileDelta);
          successes.push('profile');
        } catch (err) {
          failures.push(`profile: ${extractErrorMessage(err)}`);
        }
      }
      if (Object.keys(companyDelta).length > 0) {
        try {
          await onUpdateCompany(companyDelta);
          successes.push('company');
        } catch (err) {
          failures.push(`company: ${extractErrorMessage(err)}`);
        }
      }
      // eslint-disable-next-line no-console
      console.info('[CR-4815] save complete', { successes, failures });

      if (failures.length === 0 && successes.length === 0) {
        toast.info('No changes to save');
        closeDialog();
        return;
      }
      if (failures.length > 0 && successes.length === 0) {
        toast.error(`Failed to update supplier: ${failures.join('; ')}`);
        // Keep dialog open so the user can fix the form
        return;
      }
      if (failures.length > 0) {
        toast.warning(
          `Partial save — ${successes.join(', ')} updated, ${failures.join('; ')} failed`,
        );
      } else {
        toast.success('Supplier details updated successfully');
      }
      closeDialog();
    } catch (err: unknown) {
      // Defensive catch — should not be reachable now that each await is
      // individually try/caught, but keep so unexpected throws still
      // surface a toast instead of disappearing silently.
      // eslint-disable-next-line no-console
      console.error('[CR-4815] handleSave unexpected error', err);
      toast.error(`Unexpected error: ${extractErrorMessage(err)}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        {/* Company Information */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Company Information
              </CardTitle>
              {/* CR-4819: view + edit live together on Overview now */}
              {canEdit && (
                <Button variant="outline" size="sm" onClick={openEditDialog}>
                  <Pencil className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {/* CR-6681: two-column layout. Left = primary contact / identity
                (Email, Telephone, Company Number, Location, Website); right =
                the remaining payment / business fields. */}
            <div className="grid grid-cols-1 gap-x-8 gap-y-4 md:grid-cols-2">
              {/* Left column */}
              <div className="space-y-4">
                {cd.primary_email && <InfoRow icon={Mail} label="Email">{cd.primary_email}</InfoRow>}
                {/* The named person to call about this supplier, as opposed to
                    the company address above. Read-only here — designated in
                    Users Management or on the supplier's Users tab. */}
                <PrimaryContactRow supplier={supplier} />
                {displayPhone && <InfoRow icon={Phone} label="Telephone">{displayPhone}</InfoRow>}
                <InfoRow icon={Hash} label="Company Number">{cd.registration_number || 'Not specified'}</InfoRow>
                {fullAddress && <InfoRow icon={MapPin} label="Location">{fullAddress}</InfoRow>}
                {cd.website && (
                  <InfoRow icon={Globe} label="Website">
                    <a href={cd.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                      {cd.website}
                    </a>
                  </InfoRow>
                )}
              </div>
              {/* Right column.
                  Note: Supplier Code is deliberately NOT shown here — CR-6368
                  removes it from the overview; omitting it keeps this layout
                  aligned with that change. */}
              <div className="space-y-4">
                {/* CR-6681: Payment Terms surfaced on the Overview card. */}
                <InfoRow icon={Clock} label="Payment Terms">{supplier.payment_terms || 'Not specified'}</InfoRow>
                {supplier.currency && <InfoRow icon={DollarSign} label="Currency">{supplier.currency}</InfoRow>}
                {supplier.annual_revenue && (
                  <InfoRow icon={DollarSign} label="Annual Revenue">
                    {/* CR-4815 r6: render in the supplier's currency, not the viewer's */}
                    {formatCurrency(Number(supplier.annual_revenue), supplier.currency || undefined)}
                  </InfoRow>
                )}
                {supplier.employee_count && <InfoRow icon={Users} label="Employees">{supplier.employee_count} employees</InfoRow>}
                {supplier.year_established && <InfoRow icon={Calendar} label="Established">{supplier.year_established}</InfoRow>}
                {cd.description && <InfoRow icon={FileText} label="Description">{cd.description}</InfoRow>}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* CR-4821: Performance Summary removed from Company Overview;
            all performance metrics now live only on the Performance tab. */}
      </div>

      {/* Capabilities & Certifications */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* CR-4820: Capabilities live here with inline edit — no longer duplicated on Settings */}
        {showCapabilities && (
          <CapabilitiesEditor
            supplier={supplier}
            canEdit={canEdit}
            onUpdateProfile={onUpdateProfile}
          />
        )}

        {/* Certifications */}
        {supplier.certifications.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Award className="h-5 w-5" />
                Certifications
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {supplier.certifications.map((cert, idx) => (
                  <Badge key={idx} variant="outline" className="bg-blue-50 dark:bg-blue-950">
                    {cert}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* CR-4819: Company Information edit dialog (was on Settings, moved here) */}
      <EditCompanyDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        editForm={editForm}
        setEditForm={setEditForm}
        saving={saving}
        onSave={handleSave}
      />

      {/* Audit Information */}
      {(supplier.last_audit_date || supplier.next_audit_date) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Audit Information
            </CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 md:grid-cols-2">
            {supplier.last_audit_date && (
              <div>
                <p className="text-sm font-medium">Last Audit</p>
                <p className="text-sm text-muted-foreground">
                  {formatDate(supplier.last_audit_date)}
                </p>
              </div>
            )}
            {supplier.next_audit_date && (
              <div>
                <p className="text-sm font-medium">Next Audit</p>
                <p className="text-sm text-muted-foreground">
                  {formatDate(supplier.next_audit_date)}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
